import React from 'react';

import { useKeyboardInset } from '../hooks/useKeyboardInset';

/**
 * Gabarit d'écran : en-tête figé, corps défilant, pied optionnel.
 *
 * C'est ici que le socle « confisque la hauteur » (#34). La chaîne `h-full`
 * remonte jusqu'à `html` via `mobile.css` — jamais `h-screen`, jamais `100vh`,
 * jamais `100dvh` : aucun des trois ne voit le clavier.
 *
 * Le décalage clavier est appliqué en `padding-bottom` sur la colonne, pas en
 * translation ni en hauteur : la colonne occupe le viewport de mise en page, la
 * rogner par le bas fait remonter le pied d'autant. Aucune superposition n'est
 * impliquée, donc aucune double compensation possible.
 *
 * Un seul défileur, celui du corps : #22 a écarté tout défilement imbriqué, qui
 * se dispute le balayage de retour — seul retour d'une PWA iOS (#17).
 */
interface ScreenProps {
    children: React.ReactNode;
    entete?: React.ReactNode;
    /** Barre basse : saisie, action principale. C'est elle que le clavier menace. */
    pied?: React.ReactNode;
    /** Le conteneur qui défile, pour qui doit lire ou restaurer sa position. */
    refCorps?: React.Ref<HTMLDivElement>;
    /**
     * Coupe le rebond natif d'iOS sur le corps : requis quand l'écran porte le geste
     * « glisser pour actualiser », sinon contenu et indicateur descendent ensemble.
     */
    sansRebond?: boolean;
}

export default function Screen({ children, entete, pied, refCorps, sansRebond }: ScreenProps) {
    const decalageClavier = useKeyboardInset();

    return (
        <div
            className="flex h-full flex-col bg-main-bg-color"
            style={{ paddingBottom: decalageClavier }}
            data-testid="tf-screen"
        >
            {entete ? <div className="shrink-0">{entete}</div> : null}

            <div
                ref={refCorps}
                className={`min-h-0 flex-1 overflow-y-auto ${sansRebond ? 'overscroll-y-none' : ''}`}
            >
                {children}
            </div>

            {pied ? <div className="shrink-0">{pied}</div> : null}
        </div>
    );
}
